
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Coins, MapPin, Phone, Truck, Star, Trees } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

const AchatMetauxLozere = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="Achat Métaux Lozère (48) - Mende, Florac | Groupe Fabre"
        description="Rachat de métaux et ferrailles en Lozère : Mende, Florac, Marvejols. Solutions montagne, parcs naturels, service adapté au rural."
        canonical="/nos-services/achat-metaux/lozere"
        keywords="achat métaux Lozère, rachat ferraille Mende, achat cuivre Florac, ferrailleur Lozère 48"
        localBusiness={{ city: "Mende", region: "Lozère", postalCode: "48000", serviceType: "Achat de métaux" }}
      />
      <Navbar />
      
      <main className="flex-1 mt-16">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center mb-4">
                <Trees className="h-12 w-12 mr-4" />
                <div>
                  <h1 className="text-4xl md:text-5xl font-bold">Achat Métaux Lozère (48)</h1>
                  <p className="text-xl mt-2 opacity-90">Parcs naturels - Solutions préservation</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-fabre-gray mb-6">
                  Rachat de métaux en Lozère - Territoire préservé
                </h2>
                <p className="text-gray-600 mb-6">
                  La <strong>Lozère</strong> est un territoire d'exception avec ses parcs naturels protégés. 
                  <strong>Groupe Fabre Environnement</strong> adapte ses interventions pour respecter 
                  cet environnement unique tout en proposant des <strong>solutions efficaces</strong> 
                  pour la valorisation de vos métaux.
                </p>
                
                <h3 className="text-xl font-bold text-fabre-gray mb-4">Zones de couverture Lozère</h3>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-emerald-600 text-white mr-3">•</span>
                    <span><strong>Mende :</strong> Préfecture et Gévaudan</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-emerald-600 text-white mr-3">•</span>
                    <span><strong>Florac :</strong> Cœur des Cévennes</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-emerald-600 text-white mr-3">•</span>
                    <span><strong>Marvejols :</strong> Terre de loups</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-emerald-600 text-white mr-3">•</span>
                    <span><strong>Langogne :</strong> Margeride et Allier</span>
                  </li>
                </ul>

                <div className="bg-emerald-50 border border-emerald-200 p-6 rounded-lg">
                  <h4 className="font-bold text-emerald-800 mb-3">🌲 Respect environnemental</h4>
                  <ul className="text-emerald-700 text-sm space-y-2">
                    <li>• Interventions respectueuses des parcs naturels</li>
                    <li>• Transport optimisé pour réduire l'impact</li>
                    <li>• Partenariats avec acteurs locaux</li>
                    <li>• Service adapté aux contraintes territoriales</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-fabre-gray mb-6">Service Lozère</h3>
                <p className="text-gray-600 mb-6">
                  Notre service en Lozère privilégie une approche respectueuse de l'environnement 
                  tout en maintenant un service de qualité.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center">
                    <Trees className="h-5 w-5 text-emerald-600 mr-3" />
                    <span>Solutions éco-responsables</span>
                  </div>
                  <div className="flex items-center">
                    <Coins className="h-5 w-5 text-emerald-600 mr-3" />
                    <span>Prix équitables ruraux</span>
                  </div>
                  <div className="flex items-center">
                    <Star className="h-5 w-5 text-emerald-600 mr-3" />
                    <span>Service nature préservée</span>
                  </div>
                </div>

                <Link to="/contact" className="block">
                  <button className="w-full px-6 py-3 bg-emerald-600 text-white rounded-md font-medium hover:bg-emerald-700 transition-colors">
                    Demander prix Lozère
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Spécificités Lozère */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-fabre-gray mb-8 text-center">
              Adaptations territoire Lozère
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Tourisme Vert</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Équipements touristiques, hébergements ruraux
                </p>
                <div className="text-green-600 font-medium">Service discret</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Agriculture Montagne</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Matériel spécialisé, équipements d'élevage
                </p>
                <div className="text-orange-600 font-medium">Tarifs montagnards</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Parcs Naturels</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Équipements scientifiques, aménagements
                </p>
                <div className="text-emerald-600 font-medium">Expertise environnementale</div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AchatMetauxLozere;
